import type {
  TurnWorld, Scenario, PlayerId, EventAction, EventCondition,
  EventTrigger, VictoryCondition, DefeatCondition,
  TutorialValidation, TutorialStep, OutcomeConfig,
} from './types';

/** Action descriptor passed to isActionAllowed for tutorial blocking. */
export type PlayerAction =
  | { type: 'move'; unitId: number; to: { col: number; row: number } }
  | { type: 'attack'; unitId: number; targetId: number }
  | { type: 'capture'; unitId: number; buildingId: number }
  | { type: 'produce'; buildingId: number; unitType: string }
  | { type: 'end-turn' }
  | { type: 'select-unit'; unitId: number }
  | { type: 'select-building'; buildingId: number }
  | { type: 'heal'; unitId: number }
  | { type: 'resupply'; unitId: number }
  | { type: 'load'; unitId: number; carrierId: number }
  | { type: 'unload'; carrierId: number }
  | { type: 'build'; unitId: number };

export interface TutorialState {
  step: TutorialStep;
  stepIndex: number;
  totalSteps: number;
  finished: boolean;
}

/**
 * ScenarioRunner — pure-logic observer that evaluates victory/defeat conditions,
 * fires scripted events, manages tutorial steps, and grades outcomes.
 * No Phaser dependency. TurnGameScene calls hooks and processes pending actions.
 */
/** Live status of one scenario objective, for the HUD objectives panel. */
export interface ObjectiveStatus {
  kind: 'victory' | 'defeat';
  condition: VictoryCondition | DefeatCondition;
  /** Victory: condition met. Defeat: condition triggered (bad). */
  done: boolean;
  /** Numeric progress numerator, or null when the objective has no count. */
  current: number | null;
  /** Numeric progress denominator, or null when the objective has no count. */
  total: number | null;
}

export class ScenarioRunner {
  private world: TurnWorld;
  private scenario: Scenario;
  private tagMap: Map<string, number>;

  // Event system state
  private firedEvents = new Set<number>();
  private flags = new Set<string>();
  private pendingActions: EventAction[] = [];

  // Hold-buildings tracking: buildingTag → consecutive turns held (legacy
  // `hold-buildings` victory condition — human-player only).
  private holdCounters = new Map<string, number>();

  // Hold-building / fail-hold-building tracking — remembers who's holding
  // the tagged building right now and for how many consecutive turns.
  // Counter resets and player changes when ownership flips.
  private holdBuildingState = new Map<string, { player: PlayerId | null; count: number }>();

  // Hold-group / fail-hold-group tracking — same idea but for full groups
  // (counter only ticks while a single player owns every member).
  private holdGroupState = new Map<string, { player: PlayerId | null; count: number }>();

  // Tutorial state
  private tutorialStepIndex = 0;

  // Group tutorial state — tracks any-order step groups
  private groupCompletedBranches = new Set<string>();
  private activeBranch: string | null = null;
  private activeBranchProgress = 0;

  // Outcome stats
  private unitsLost = 0;
  private buildingsCaptured = 0;

  // Result cache (once determined, stays)
  private result: 'victory' | 'defeat' | null = null;

  constructor(world: TurnWorld, scenario: Scenario, tagMap: Map<string, number>) {
    this.world = world;
    this.scenario = scenario;
    this.tagMap = tagMap;
  }

  // ─── Public hooks ───────────────────────────────────────────────

  onTurnStart(turn: number, player: PlayerId): void {
    if (this.result) return;
    this.evaluateEvents({ type: 'turn-start', turn, player });
    if (!this.result) this.result = this.checkConditions();
  }

  onTurnEnd(turn: number, player: PlayerId): void {
    if (this.result) return;
    this.advanceHoldCounters();
    this.evaluateEvents({ type: 'turn-end', turn, player });
    if (!this.result) this.result = this.checkConditions();
  }

  onUnitKilled(unitId: number): void {
    if (this.result) return;
    this.unitsLost++;
    const tag = this.getTagForId(unitId);
    if (tag) {
      this.evaluateEvents({ type: 'unit-killed', unitTag: tag });
    }
    if (!this.result) this.result = this.checkConditions();
  }

  onBuildingCaptured(buildingId: number, _newOwner: PlayerId): void {
    if (this.result) return;
    this.buildingsCaptured++;
    const tag = this.getTagForId(buildingId);
    if (tag) {
      this.evaluateEvents({ type: 'building-captured', buildingTag: tag });
    }
    // Reset hold counters for buildings that changed hands
    this.resetHoldCountersForBuilding(buildingId);
    if (!this.result) this.result = this.checkConditions();
  }

  onUnitMoved(unitId: number, _from: { col: number; row: number }, to: { col: number; row: number }): void {
    if (this.result) return;
    const tag = this.getTagForId(unitId);
    this.evaluateEventsForUnitMove(tag, to);
    if (!this.result) this.result = this.checkConditions();
  }

  // ─── Public queries ─────────────────────────────────────────────

  checkResult(): 'victory' | 'defeat' | null {
    return this.result;
  }

  /** Live status of every victory and defeat objective — for the HUD panel.
   *  Returns an empty list when the scenario defines no explicit objectives. */
  getObjectives(): ObjectiveStatus[] {
    const out: ObjectiveStatus[] = [];
    for (const c of this.scenario.victoryConditions ?? []) {
      const p = this.victoryProgress(c);
      out.push({ kind: 'victory', condition: c, done: this.evaluateVictoryCondition(c), current: p.current, total: p.total });
    }
    for (const c of this.scenario.defeatConditions ?? []) {
      const p = this.defeatProgress(c);
      out.push({ kind: 'defeat', condition: c, done: this.evaluateDefeatCondition(c), current: p.current, total: p.total });
    }
    return out;
  }

  private groupSize(groupId: string): number {
    return this.scenario.buildingGroups?.find(g => g.id === groupId)?.buildingTags.length ?? 0;
  }

  private groupOwnedCount(groupId: string, player: PlayerId): number {
    const group = this.scenario.buildingGroups?.find(g => g.id === groupId);
    if (!group) return 0;
    let n = 0;
    for (const tag of group.buildingTags) {
      const id = this.tagMap.get(tag);
      const b = id !== undefined ? this.world.buildings.get(id) : undefined;
      if (b && b.owner === player) n++;
    }
    return n;
  }

  private victoryProgress(c: VictoryCondition): { current: number | null; total: number | null } {
    const human = this.world.humanPlayer;
    switch (c.type) {
      case 'survive':
      case 'protect-unit':
        return { current: Math.min(this.world.turnNumber, c.turns), total: c.turns };
      case 'hold-buildings': {
        let lo = c.turns;
        for (const tag of c.buildingTags) lo = Math.min(lo, this.holdCounters.get(tag) ?? 0);
        return { current: lo, total: c.turns };
      }
      case 'hold-building': {
        const st = this.holdBuildingState.get(c.buildingTag);
        return { current: st && st.player === human ? st.count : 0, total: c.forTurns };
      }
      case 'hold-group': {
        const st = this.holdGroupState.get(c.groupId);
        return { current: st && st.player === human ? st.count : 0, total: c.forTurns };
      }
      case 'capture-group':
        return { current: this.groupOwnedCount(c.groupId, human), total: this.groupSize(c.groupId) };
      default:
        return { current: null, total: null };
    }
  }

  private defeatProgress(c: DefeatCondition): { current: number | null; total: number | null } {
    const human = this.world.humanPlayer;
    switch (c.type) {
      case 'turn-limit':
        return { current: Math.min(this.world.turnNumber, c.turns), total: c.turns };
      case 'lose-group':
        return { current: this.groupOwnedCount(c.groupId, human), total: this.groupSize(c.groupId) };
      case 'fail-hold-building': {
        const st = this.holdBuildingState.get(c.buildingTag);
        const enemyHolding = !!st && st.player != null && st.player !== human;
        return { current: enemyHolding ? st.count : 0, total: c.forTurns };
      }
      case 'fail-hold-group': {
        const st = this.holdGroupState.get(c.groupId);
        const enemyHolding = !!st && st.player != null && st.player !== human;
        return { current: enemyHolding ? st.count : 0, total: c.forTurns };
      }
      default:
        return { current: null, total: null };
    }
  }

  getPendingActions(): EventAction[] {
    const actions = this.pendingActions;
    this.pendingActions = [];
    return actions;
  }

  getTutorialState(): TutorialState | null {
    const tut = this.scenario.tutorial;
    if (!tut) return null;
    const finished = this.tutorialStepIndex >= tut.steps.length;
    if (finished) {
      return {
        step: tut.steps[tut.steps.length - 1],
        stepIndex: this.tutorialStepIndex,
        totalSteps: tut.steps.length,
        finished: true,
      };
    }

    const currentStep = tut.steps[this.tutorialStepIndex];

    // Not in a group — standard linear behaviour
    if (!currentStep.group) {
      return {
        step: currentStep,
        stepIndex: this.tutorialStepIndex,
        totalSteps: tut.steps.length,
        finished: false,
      };
    }

    // In a group — compute effective step and progress
    const groupId = currentStep.group;
    const branches = this.getGroupBranches(groupId);

    if (this.activeBranch) {
      // Active branch — show the current step within the branch
      const branchSteps = branches.get(this.activeBranch)!;
      const effectiveStep = branchSteps[this.activeBranchProgress];
      return {
        step: effectiveStep,
        stepIndex: this.computeGroupProgress(groupId, branches),
        totalSteps: tut.steps.length,
        finished: false,
      };
    }

    // No active branch — show group-level "pick" instruction
    const groupConfig = tut.groups?.[groupId];
    if (groupConfig) {
      const syntheticStep: TutorialStep = {
        instruction: groupConfig.instruction,
        highlight: groupConfig.highlight,
        validation: { type: 'turn-ended' }, // placeholder, not used for matching
      };
      return {
        step: syntheticStep,
        stepIndex: this.computeGroupProgress(groupId, branches),
        totalSteps: tut.steps.length,
        finished: false,
      };
    }

    // Fallback: show first available branch's first step
    for (const [branchId, branchSteps] of branches) {
      if (!this.groupCompletedBranches.has(branchId)) {
        return {
          step: branchSteps[0],
          stepIndex: this.computeGroupProgress(groupId, branches),
          totalSteps: tut.steps.length,
          finished: false,
        };
      }
    }

    // All branches done (shouldn't reach here — group should advance)
    return {
      step: currentStep,
      stepIndex: this.tutorialStepIndex,
      totalSteps: tut.steps.length,
      finished: false,
    };
  }

  /** Returns true if the action is allowed (tutorial blocking). */
  isActionAllowed(action: PlayerAction): boolean {
    const tut = this.scenario.tutorial;
    if (!tut) return true;
    if (this.tutorialStepIndex >= tut.steps.length) return true;

    const currentStep = tut.steps[this.tutorialStepIndex];

    // Not in a group — standard linear behaviour
    if (!currentStep.group) {
      if (currentStep.blocking === false) return true;
      if (this.matchesValidation(action, currentStep.validation)) return true;
      return this.isReselectAllowed(action, currentStep);
    }

    // In a group
    const groupId = currentStep.group;
    const branches = this.getGroupBranches(groupId);

    if (this.activeBranch) {
      // Active branch — check current branch step
      const branchSteps = branches.get(this.activeBranch)!;
      const step = branchSteps[this.activeBranchProgress];
      if (step.blocking === false) return true;
      if (this.matchesValidation(action, step.validation)) return true;
      return this.isReselectAllowed(action, step);
    }

    // No active branch — allow first step of any uncompleted branch
    for (const [branchId, branchSteps] of branches) {
      if (this.groupCompletedBranches.has(branchId)) continue;
      if (this.matchesValidation(action, branchSteps[0].validation)) return true;
    }
    return false;
  }

  /** Call when a player action completes to check tutorial step advancement. */
  onActionCompleted(action: PlayerAction): void {
    const tut = this.scenario.tutorial;
    if (!tut || this.tutorialStepIndex >= tut.steps.length) return;

    const currentStep = tut.steps[this.tutorialStepIndex];

    // Not in a group — standard linear behaviour
    if (!currentStep.group) {
      if (this.matchesValidation(action, currentStep.validation)) {
        if (currentStep.onComplete) {
          this.executeActions(currentStep.onComplete);
        }
        this.tutorialStepIndex++;
      }
      return;
    }

    // In a group
    const groupId = currentStep.group;
    const branches = this.getGroupBranches(groupId);

    if (this.activeBranch) {
      // Active branch — try to advance within it
      const branchSteps = branches.get(this.activeBranch)!;
      const step = branchSteps[this.activeBranchProgress];
      if (this.matchesValidation(action, step.validation)) {
        if (step.onComplete) {
          this.executeActions(step.onComplete);
        }
        this.activeBranchProgress++;

        // Check if branch is complete
        if (this.activeBranchProgress >= branchSteps.length) {
          this.groupCompletedBranches.add(this.activeBranch);
          this.activeBranch = null;
          this.activeBranchProgress = 0;
          this.tryCompleteGroup(groupId, branches);
        }
      }
      return;
    }

    // No active branch — try to start one
    for (const [branchId, branchSteps] of branches) {
      if (this.groupCompletedBranches.has(branchId)) continue;
      if (this.matchesValidation(action, branchSteps[0].validation)) {
        if (branchSteps[0].onComplete) {
          this.executeActions(branchSteps[0].onComplete);
        }
        this.activeBranch = branchId;
        this.activeBranchProgress = 1; // first step already done

        // Single-step branch? Complete it immediately
        if (this.activeBranchProgress >= branchSteps.length) {
          this.groupCompletedBranches.add(branchId);
          this.activeBranch = null;
          this.activeBranchProgress = 0;
          this.tryCompleteGroup(groupId, branches);
        }
        return;
      }
    }
  }

  evaluateOutcome(): number {
    const cfg = this.scenario.outcome;
    if (!cfg) return 0;
    if (this.matchesThreshold(cfg.threeStars)) return 3;
    if (this.matchesThreshold(cfg.twoStars)) return 2;
    return 1; // oneStar always matches on victory
  }

  // ─── Group helpers ──────────────────────────────────────────────

  /** Collect steps belonging to a group, organized by branch. */
  private getGroupBranches(groupId: string): Map<string, TutorialStep[]> {
    const branches = new Map<string, TutorialStep[]>();
    const steps = this.scenario.tutorial!.steps;
    for (let i = 0; i < steps.length; i++) {
      if (steps[i].group !== groupId) continue;
      const branch = steps[i].branch ?? '';
      if (!branches.has(branch)) branches.set(branch, []);
      branches.get(branch)!.push(steps[i]);
    }
    return branches;
  }

  /** Find the first index after all steps of a group. */
  private getGroupEndIndex(groupId: string): number {
    const steps = this.scenario.tutorial!.steps;
    let last = this.tutorialStepIndex;
    for (let i = this.tutorialStepIndex; i < steps.length; i++) {
      if (steps[i].group === groupId) last = i;
    }
    return last + 1;
  }

  /** Compute a step-progress index for display within a group. */
  private computeGroupProgress(_groupId: string, branches: Map<string, TutorialStep[]>): number {
    let done = 0;
    for (const [branchId, branchSteps] of branches) {
      if (this.groupCompletedBranches.has(branchId)) {
        done += branchSteps.length;
      }
    }
    done += this.activeBranchProgress;
    return this.tutorialStepIndex + done;
  }

  /** When all branches are done, advance past the group and fire group onComplete. */
  private tryCompleteGroup(groupId: string, branches: Map<string, TutorialStep[]>): void {
    for (const branchId of branches.keys()) {
      if (!this.groupCompletedBranches.has(branchId)) return; // not all done
    }
    // All branches complete — fire group onComplete and advance
    const groupConfig = this.scenario.tutorial?.groups?.[groupId];
    if (groupConfig?.onComplete) {
      this.executeActions(groupConfig.onComplete);
    }
    this.tutorialStepIndex = this.getGroupEndIndex(groupId);
    this.groupCompletedBranches.clear();
    this.activeBranch = null;
    this.activeBranchProgress = 0;
  }

  /**
   * Special blocking exception: allow re-selecting a player unit or building
   * needed for the current step so the player can recover after accidentally
   * deselecting.
   */
  private isReselectAllowed(action: PlayerAction, step: TutorialStep): boolean {
    // Allow re-selecting a building for produce steps (e.g. recruitment tutorial)
    if (action.type === 'select-building') {
      if (step.validation.type === 'unit-produced') {
        return this.tagMap.get(step.validation.buildingTag) === action.buildingId;
      }
      return false;
    }

    if (action.type !== 'select-unit') return false;

    // For building-captured: allow selecting the unit on that building
    if (step.validation.type === 'building-captured') {
      const buildingId = this.tagMap.get(step.validation.buildingTag);
      if (buildingId === undefined) return false;
      const building = this.world.buildings.get(buildingId);
      const unit = this.world.units.get(action.unitId);
      return !!building && !!unit
        && unit.col === building.col && unit.row === building.row
        && unit.owner === this.world.humanPlayer;
    }

    // For heal/resupply/build: allow re-selecting the actor unit
    if (step.validation.type === 'unit-healed'
      || step.validation.type === 'unit-resupplied'
      || step.validation.type === 'building-built') {
      return this.tagMap.get(step.validation.unitTag) === action.unitId;
    }

    return false;
  }

  // ─── Victory / Defeat condition evaluation ──────────────────────

  private checkConditions(): 'victory' | 'defeat' | null {
    if (this.checkDefeat()) return 'defeat';
    if (this.checkVictory()) return 'victory';
    return null;
  }

  private checkVictory(): boolean {
    const conditions = this.scenario.victoryConditions;
    if (!conditions || conditions.length === 0) return false;
    // OR — any one satisfied
    return conditions.some(c => this.evaluateVictoryCondition(c));
  }

  private checkDefeat(): boolean {
    const conditions = this.scenario.defeatConditions;
    if (!conditions || conditions.length === 0) return false;
    // OR — any one satisfied
    return conditions.some(c => this.evaluateDefeatCondition(c));
  }

  private evaluateVictoryCondition(c: VictoryCondition): boolean {
    const human = this.world.humanPlayer;
    switch (c.type) {
      case 'capture-hq': {
        // All enemy HQs captured (owned by human or destroyed)
        for (const b of this.world.buildings.values()) {
          if (b.type === 'hq' && b.owner !== human && b.owner !== null) return false;
        }
        return true;
      }
      case 'eliminate-all': {
        for (const u of this.world.units.values()) {
          if (u.owner !== human) return false;
        }
        return this.world.units.size > 0;
      }
      case 'survive':
        return this.world.turnNumber >= c.turns;
      case 'hold-buildings': {
        for (const tag of c.buildingTags) {
          const count = this.holdCounters.get(tag) ?? 0;
          if (count < c.turns) return false;
        }
        return true;
      }
      case 'kill-unit': {
        const id = this.tagMap.get(c.unitTag);
        if (id === undefined) return false;
        return !this.world.units.has(id);
      }
      case 'reach-tile': {
        const id = this.tagMap.get(c.unitTag);
        if (id === undefined) return false;
        const u = this.world.units.get(id);
        return !!u && u.col === c.col && u.row === c.row;
      }
      case 'protect-unit': {
        const id = this.tagMap.get(c.unitTag);
        if (id === undefined) return false;
        return this.world.units.has(id) && this.world.turnNumber >= c.turns;
      }
      case 'capture-building': {
        const id = this.tagMap.get(c.buildingTag);
        if (id === undefined) return false;
        const b = this.world.buildings.get(id);
        return !!b && b.owner === human;
      }
      case 'capture-group':
        return this.playerControlsGroup(c.groupId, human);
      case 'hold-building': {
        const st = this.holdBuildingState.get(c.buildingTag);
        return !!st && st.player === human && st.count >= c.forTurns;
      }
      case 'hold-group': {
        const st = this.holdGroupState.get(c.groupId);
        return !!st && st.player === human && st.count >= c.forTurns;
      }
    }
  }

  /** True iff every building tagged in `groupId` is owned by `playerId`. */
  private playerControlsGroup(groupId: string, playerId: PlayerId): boolean {
    const group = this.scenario.buildingGroups?.find(g => g.id === groupId);
    if (!group || group.buildingTags.length === 0) return false;
    for (const tag of group.buildingTags) {
      const id = this.tagMap.get(tag);
      if (id === undefined) return false;
      const b = this.world.buildings.get(id);
      if (!b || b.owner !== playerId) return false;
    }
    return true;
  }

  /** True iff at least one tagged building of the group is still owned by `playerId`. */
  private playerOwnsAnyOfGroup(groupId: string, playerId: PlayerId): boolean {
    const group = this.scenario.buildingGroups?.find(g => g.id === groupId);
    if (!group) return false;
    for (const tag of group.buildingTags) {
      const id = this.tagMap.get(tag);
      if (id === undefined) continue;
      const b = this.world.buildings.get(id);
      if (b && b.owner === playerId) return true;
    }
    return false;
  }

  private evaluateDefeatCondition(c: DefeatCondition): boolean {
    const human = this.world.humanPlayer;
    switch (c.type) {
      case 'unit-killed': {
        const id = this.tagMap.get(c.unitTag);
        if (id === undefined) return false;
        return !this.world.units.has(id);
      }
      case 'building-lost': {
        const id = this.tagMap.get(c.buildingTag);
        if (id === undefined) return false;
        // A building that the scenario explicitly assigns to ANOTHER player at
        // start was never the human's to "lose" — don't instantly defeat the
        // player who happens to be on the other side of an asymmetric map.
        // (When the scenario doesn't declare an owner, keep the legacy
        // "enemy-owned tag = defeat" behaviour.)
        const initial = this.scenario.buildings.find(sb => sb.tag === c.buildingTag);
        if (initial && initial.owner !== null && initial.owner !== human) return false;
        const b = this.world.buildings.get(id);
        return !!b && b.owner !== human && b.owner !== null;
      }
      case 'turn-limit':
        return this.world.turnNumber > c.turns;
      case 'all-units-killed': {
        for (const u of this.world.units.values()) {
          if (u.owner === human) return false;
        }
        return true;
      }
      case 'lose-group':
        // Defeat when the player has lost ALL buildings of the group — i.e.
        // none of them is owned by the human player anymore. (For the human
        // to "lose" a neutral group means it never owned any in the first
        // place; this condition triggers after the last building flips out.)
        return !this.playerOwnsAnyOfGroup(c.groupId, human);
      case 'fail-hold-building': {
        // Defeat when somebody other than the human has held the building
        // for `forTurns` consecutive turns — mirror of `hold-building` from
        // the opposite side. Neutral ownership doesn't count.
        const st = this.holdBuildingState.get(c.buildingTag);
        return !!st && st.player != null && st.player !== human && st.count >= c.forTurns;
      }
      case 'fail-hold-group': {
        const st = this.holdGroupState.get(c.groupId);
        return !!st && st.player != null && st.player !== human && st.count >= c.forTurns;
      }
    }
  }

  // ─── Hold-buildings tracking ────────────────────────────────────

  private advanceHoldCounters(): void {
    const human = this.world.humanPlayer;

    // Legacy `hold-buildings` victory condition (human-player only).
    const victory = this.scenario.victoryConditions;
    if (victory) {
      for (const c of victory) {
        if (c.type !== 'hold-buildings') continue;
        for (const tag of c.buildingTags) {
          const id = this.tagMap.get(tag);
          if (id === undefined) continue;
          const b = this.world.buildings.get(id);
          if (b && b.owner === human) {
            this.holdCounters.set(tag, (this.holdCounters.get(tag) ?? 0) + 1);
          } else {
            this.holdCounters.set(tag, 0);
          }
        }
      }
    }

    // New per-tag and per-group hold tracking — used by hold-building,
    // hold-group, fail-hold-building, fail-hold-group. Each `*State` map
    // remembers the last owning player; if it changes, counter resets.
    const tags = this.collectHoldBuildingTags();
    for (const tag of tags) {
      const id = this.tagMap.get(tag);
      const b = id !== undefined ? this.world.buildings.get(id) : undefined;
      const owner: PlayerId | null = b ? b.owner : null;
      const prev = this.holdBuildingState.get(tag);
      if (prev && prev.player === owner && owner !== null) {
        prev.count += 1;
      } else {
        this.holdBuildingState.set(tag, { player: owner, count: owner !== null ? 1 : 0 });
      }
    }

    const groupIds = this.collectHoldGroupIds();
    for (const groupId of groupIds) {
      const owner = this.groupSingleOwner(groupId);
      const prev = this.holdGroupState.get(groupId);
      if (prev && prev.player === owner && owner !== null) {
        prev.count += 1;
      } else {
        this.holdGroupState.set(groupId, { player: owner, count: owner !== null ? 1 : 0 });
      }
    }
  }

  /** Collect every buildingTag referenced by a hold-* / fail-hold-* condition. */
  private collectHoldBuildingTags(): Set<string> {
    const out = new Set<string>();
    for (const c of this.scenario.victoryConditions ?? []) {
      if (c.type === 'hold-building') out.add(c.buildingTag);
    }
    for (const c of this.scenario.defeatConditions ?? []) {
      if (c.type === 'fail-hold-building') out.add(c.buildingTag);
    }
    return out;
  }

  /** Collect every groupId referenced by a hold-group / fail-hold-group condition. */
  private collectHoldGroupIds(): Set<string> {
    const out = new Set<string>();
    for (const c of this.scenario.victoryConditions ?? []) {
      if (c.type === 'hold-group') out.add(c.groupId);
    }
    for (const c of this.scenario.defeatConditions ?? []) {
      if (c.type === 'fail-hold-group') out.add(c.groupId);
    }
    return out;
  }

  /** Returns the player who owns EVERY building in the group, or null when
   *  ownership is mixed / partly neutral / group missing. */
  private groupSingleOwner(groupId: string): PlayerId | null {
    const group = this.scenario.buildingGroups?.find(g => g.id === groupId);
    if (!group || group.buildingTags.length === 0) return null;
    let owner: PlayerId | null | undefined = undefined;
    for (const tag of group.buildingTags) {
      const id = this.tagMap.get(tag);
      if (id === undefined) return null;
      const b = this.world.buildings.get(id);
      if (!b) return null;
      if (owner === undefined) owner = b.owner;
      else if (owner !== b.owner) return null;
    }
    return owner === undefined ? null : owner;
  }

  private resetHoldCountersForBuilding(buildingId: number): void {
    const tag = this.getTagForId(buildingId);
    if (!tag) return;
    // Legacy `hold-buildings` counter.
    if (this.holdCounters.has(tag)) this.holdCounters.set(tag, 0);
    // New per-tag hold tracking — the per-state map is rebuilt every turn
    // start in advanceHoldCounters, but a mid-turn capture still needs to
    // clear it so a `hold-building` evaluation later this turn doesn't see
    // stale data.
    if (this.holdBuildingState.has(tag)) {
      this.holdBuildingState.set(tag, { player: null, count: 0 });
    }
    // Group counters — any group containing this tag is broken.
    for (const group of this.scenario.buildingGroups ?? []) {
      if (group.buildingTags.includes(tag) && this.holdGroupState.has(group.id)) {
        this.holdGroupState.set(group.id, { player: null, count: 0 });
      }
    }
  }

  // ─── Event system ─────────────────────────────────────────────

  private evaluateEvents(trigger: { type: string; turn?: number; player?: PlayerId; unitTag?: string; buildingTag?: string }): void {
    const events = this.scenario.events;
    if (!events) return;
    for (let i = 0; i < events.length; i++) {
      if (this.firedEvents.has(i) && (events[i].once !== false)) continue;
      if (!this.matchTrigger(events[i].trigger, trigger)) continue;
      if (!this.checkEventConditions(events[i].conditions)) continue;
      // Fire event
      if (events[i].once !== false) this.firedEvents.add(i);
      this.executeActions(events[i].actions);
    }
  }

  private evaluateEventsForUnitMove(unitTag: string | undefined, to: { col: number; row: number }): void {
    const events = this.scenario.events;
    if (!events) return;
    for (let i = 0; i < events.length; i++) {
      if (this.firedEvents.has(i) && (events[i].once !== false)) continue;
      const t = events[i].trigger;
      if (t.type !== 'unit-enters-area') continue;
      if (t.unitTag && t.unitTag !== unitTag) continue;
      const dx = to.col - t.col;
      const dy = to.row - t.row;
      if (dx * dx + dy * dy > t.radius * t.radius) continue;
      if (!this.checkEventConditions(events[i].conditions)) continue;
      if (events[i].once !== false) this.firedEvents.add(i);
      this.executeActions(events[i].actions);
    }
  }

  private matchTrigger(eventTrigger: EventTrigger, actual: { type: string; turn?: number; player?: PlayerId; unitTag?: string; buildingTag?: string }): boolean {
    if (eventTrigger.type !== actual.type) return false;
    switch (eventTrigger.type) {
      case 'turn-start':
      case 'turn-end':
        if (eventTrigger.turn !== undefined && eventTrigger.turn !== actual.turn) return false;
        if (eventTrigger.player !== undefined && eventTrigger.player !== actual.player) return false;
        return true;
      case 'unit-killed':
        return eventTrigger.unitTag === actual.unitTag;
      case 'building-captured':
        return eventTrigger.buildingTag === actual.buildingTag;
      case 'unit-enters-area':
        return true; // handled separately in evaluateEventsForUnitMove
    }
  }

  private checkEventConditions(conditions?: EventCondition[]): boolean {
    if (!conditions || conditions.length === 0) return true;
    return conditions.every(c => this.evaluateEventCondition(c));
  }

  private evaluateEventCondition(c: EventCondition): boolean {
    switch (c.type) {
      case 'unit-alive': {
        const id = this.tagMap.get(c.unitTag);
        return id !== undefined && this.world.units.has(id);
      }
      case 'building-owned-by': {
        const id = this.tagMap.get(c.buildingTag);
        if (id === undefined) return false;
        const b = this.world.buildings.get(id);
        return !!b && b.owner === c.owner;
      }
      case 'building-neutral': {
        const id = this.tagMap.get(c.buildingTag);
        if (id === undefined) return false;
        const b = this.world.buildings.get(id);
        return !!b && b.owner === null;
      }
      case 'turn-ge':
        return this.world.turnNumber >= c.turn;
      case 'flag':
        return this.flags.has(c.flag);
    }
  }

  private executeActions(actions: EventAction[]): void {
    for (const action of actions) {
      switch (action.type) {
        case 'set-flag':
          this.flags.add(action.flag);
          break;
        case 'spawn-units':
        case 'message':
        case 'remove-unit':
        case 'change-owner':
        case 'add-gold':
          this.pendingActions.push(action);
          break;
        case 'victory':
          this.result = 'victory';
          break;
        case 'defeat':
          this.result = 'defeat';
          break;
      }
    }
  }

  // ─── Tutorial validation ──────────────────────────────────────

  private matchesValidation(action: PlayerAction, validation: TutorialValidation): boolean {
    switch (validation.type) {
      case 'unit-moved':
        if (action.type !== 'move') return false;
        return this.tagMap.get(validation.unitTag) === action.unitId
          && action.to.col === validation.to.col
          && action.to.row === validation.to.row;
      case 'unit-attacked':
        if (action.type !== 'attack') return false;
        return this.tagMap.get(validation.unitTag) === action.unitId
          && this.tagMap.get(validation.targetTag) === action.targetId;
      case 'building-captured':
        if (action.type !== 'capture') return false;
        return this.tagMap.get(validation.buildingTag) === action.buildingId;
      case 'unit-produced':
        if (action.type !== 'produce') return false;
        return this.tagMap.get(validation.buildingTag) === action.buildingId
          && validation.unitType === action.unitType;
      case 'turn-ended':
        return action.type === 'end-turn';
      case 'unit-selected':
        if (action.type !== 'select-unit') return false;
        return this.tagMap.get(validation.unitTag) === action.unitId;
      case 'building-selected':
        if (action.type !== 'select-building') return false;
        return this.tagMap.get(validation.buildingTag) === action.buildingId;
      case 'unit-healed':
        if (action.type !== 'heal') return false;
        return this.tagMap.get(validation.unitTag) === action.unitId;
      case 'unit-resupplied':
        if (action.type !== 'resupply') return false;
        return this.tagMap.get(validation.unitTag) === action.unitId;
      case 'unit-loaded':
        if (action.type !== 'load') return false;
        return this.tagMap.get(validation.unitTag) === action.unitId
          && this.tagMap.get(validation.carrierTag) === action.carrierId;
      case 'unit-unloaded':
        if (action.type !== 'unload') return false;
        return this.tagMap.get(validation.carrierTag) === action.carrierId;
      case 'building-built':
        if (action.type !== 'build') return false;
        return this.tagMap.get(validation.unitTag) === action.unitId;
    }
  }

  // ─── Outcome grading ────────────────────────────────────────────

  private matchesThreshold(threshold: OutcomeConfig[keyof OutcomeConfig]): boolean {
    if (threshold.maxTurns !== undefined && this.world.turnNumber > threshold.maxTurns) return false;
    if (threshold.maxUnitsLost !== undefined && this.unitsLost > threshold.maxUnitsLost) return false;
    if (threshold.minBuildingsCaptured !== undefined && this.buildingsCaptured < threshold.minBuildingsCaptured) return false;
    return true;
  }

  // ─── Helpers ────────────────────────────────────────────────────

  private getTagForId(id: number): string | undefined {
    for (const [tag, tagId] of this.tagMap) {
      if (tagId === id) return tag;
    }
    return undefined;
  }
}
